import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { api, ApiError } from "@/lib/api";
import { formatCents, formatMesRef } from "@/lib/format";
import type { ResultadoImportacao } from "@/data/folha";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, Download, FileCheck2, Info, Loader2, Upload } from "lucide-react";

type Modo = "substituir" | "adicionar";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Caminho do endpoint de importação, sem query string. */
  endpoint: string;
  titulo: string;
  descricao: React.ReactNode;
  templateHref?: string;
  onSuccess: (resultado: ResultadoImportacao) => void;
}

export const UploadPlanilhaModal = ({
  open,
  onClose,
  endpoint,
  titulo,
  descricao,
  templateHref,
  onSuccess,
}: Props) => {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [modo, setModo] = useState<Modo>("substituir");
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<ResultadoImportacao | null>(null);

  const handleClose = () => {
    setArquivo(null);
    setResultado(null);
    setModo("substituir");
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setArquivo(file);
      setResultado(null);
    }
    e.target.value = "";
  };

  const enviar = async (force = false) => {
    if (!arquivo) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", arquivo);
      const query = `?modo=${modo}${force ? "&force=true" : ""}`;
      const res = await api.postForm<ResultadoImportacao>(`${endpoint}${query}`, fd);

      setResultado(res);
      onSuccess(res);

      if (res.naoCasadas === 0) {
        toast.success(`${res.casadas} linha(s) importado(s)`);
      } else {
        toast.warning(
          `${res.casadas} importado(s), ${res.naoCasadas} sem colaborador correspondente`,
        );
      }
    } catch (err) {
      // Reupload do mesmo arquivo: oferece sobrescrever em vez de barrar.
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: { code?: string; message?: string } } | undefined;
        if (body?.error?.code === "ARQUIVO_JA_IMPORTADO") {
          toast.warning(body.error.message ?? "Arquivo já importado", {
            action: { label: "Importar mesmo assim", onClick: () => void enviar(true) },
            duration: 10_000,
          });
          return;
        }
      }
      toast.error(err instanceof Error ? err.message : "Erro ao importar o arquivo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2">
              <div>{descricao}</div>
              {templateHref && (
                <a
                  href={templateHref}
                  download
                  className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                >
                  <Download className="h-3.5 w-3.5" />
                  Baixar planilha modelo
                </a>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <label
            className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-8 text-sm transition-colors
              ${arquivo ? "border-primary/40 bg-primary/5" : "border-input bg-muted/30 hover:border-primary/50 hover:bg-primary/5"}`}
          >
            {arquivo ? (
              <>
                <FileCheck2 className="h-8 w-8 text-primary" />
                <span className="font-medium text-primary">{arquivo.name}</span>
                <span className="text-xs text-muted-foreground">Clique para trocar</span>
              </>
            ) : (
              <>
                <Upload className="h-8 w-8 text-muted-foreground" />
                <span className="font-medium">Clique para selecionar a planilha</span>
                <span className="text-xs text-muted-foreground">
                  .xlsx, .xls ou .csv — separador detectado automaticamente
                </span>
              </>
            )}
            <input
              type="file"
              accept=".csv,.txt,.xlsx,.xls"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Se já houver dados desta planilha na competência
            </Label>
            <RadioGroup value={modo} onValueChange={(v) => setModo(v as Modo)}>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="substituir" id="modo-substituir" className="mt-0.5" />
                <Label htmlFor="modo-substituir" className="font-normal">
                  Substituir
                  <span className="block text-xs text-muted-foreground">
                    Apaga os lançamentos anteriores deste tipo e importa do zero
                  </span>
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="adicionar" id="modo-adicionar" className="mt-0.5" />
                <Label htmlFor="modo-adicionar" className="font-normal">
                  Adicionar
                  <span className="block text-xs text-muted-foreground">
                    Soma a esta competência, para planilhas complementares
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {resultado && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                <span>
                  {resultado.casadas} lançamento(s) importado(s) —{" "}
                  {formatCents(resultado.totalImportado)} no total
                </span>
              </div>

              {/* Só conferência: a linha entra na folha em que foi subida
                  (comissão de outubro paga em novembro é o caso normal). */}
              {resultado.outraCompetencia > 0 && (
                <div className="flex items-start gap-1.5 rounded-md border bg-muted/30 p-2 text-xs text-muted-foreground">
                  <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    <strong className="font-medium text-foreground">Só para conferir:</strong>{" "}
                    {resultado.outraCompetencia} linha(s) têm competência/data diferente desta folha
                    {resultado.competenciasDiferentes && resultado.competenciasDiferentes.length > 0 && (
                      <>
                        {" "}(
                        {resultado.competenciasDiferentes
                          .map(
                            (c) =>
                              `${/^\d{4}-\d{2}$/.test(c.competencia) ? formatMesRef(c.competencia) : c.competencia}: ${c.linhas}`,
                          )
                          .join(" · ")}
                        )
                      </>
                    )}
                    . Elas foram importadas normalmente nesta folha — se era isso mesmo, não precisa
                    fazer nada.
                  </span>
                </div>
              )}

              {resultado.conferencia && (
                <div
                  className={`rounded-md border p-2 text-xs ${
                    !resultado.conferencia.comparavel
                      ? "border-muted bg-muted/30 text-muted-foreground"
                      : resultado.conferencia.confere
                        ? "border-green-600/40 bg-green-600/5 text-green-700 dark:text-green-400"
                        : "border-destructive/40 bg-destructive/5 text-destructive"
                  }`}
                >
                  <strong>Conferência com o rodapé da planilha:</strong>{" "}
                  {formatCents(resultado.conferencia.totalPlanilha)} declarados ·{" "}
                  {formatCents(resultado.conferencia.totalImportado)} importados
                  {!resultado.conferencia.comparavel ? (
                    <span className="block">
                      A planilha mistura competências, então o total do rodapé não é comparável —
                      confira manualmente.
                    </span>
                  ) : resultado.conferencia.confere ? (
                    <span className="block">Bateu certinho.</span>
                  ) : (
                    <span className="block">
                      Diferença de {formatCents(resultado.conferencia.diferenca)} — verifique antes
                      de fechar a competência.
                    </span>
                  )}
                </div>
              )}

              <ul className="space-y-0.5 text-xs text-muted-foreground">
                <li>Cabeçalho encontrado na linha {resultado.linhaCabecalho}</li>
                {resultado.semValor > 0 && (
                  <li>{resultado.semValor} linha(s) sem valor a descontar</li>
                )}
                {resultado.clt > 0 && (
                  <li>
                    {resultado.clt} linha(s) de CLT — entram no holerite (comissão como prêmio, Amil
                    como desconto)
                  </li>
                )}
                {resultado.ignoradas > 0 && (
                  <li>{resultado.ignoradas} linha(s) de totais ou em branco descartadas</li>
                )}
              </ul>

              {resultado.naoCasadas > 0 && (
                <div className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>
                    {resultado.naoCasadas} linha(s) não foram associadas a nenhum colaborador.
                    Resolva na aba <strong>Pendências</strong> antes de fechar a competência.
                  </span>
                </div>
              )}

              {resultado.divergencias.length > 0 && (
                <div className="space-y-1 rounded-md border border-amber-500/40 bg-amber-500/5 p-2">
                  <div className="flex items-start gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      Em {resultado.divergencias.length} linha(s) o Total Geral não bate com
                      Comissão + Bônus Meta. Foi pago o <strong>Total Geral</strong>:
                    </span>
                  </div>
                  <ul className="space-y-0.5 pl-5 text-xs text-muted-foreground">
                    {resultado.divergencias.slice(0, 6).map((d) => (
                      <li key={d.linha}>
                        Linha {d.linha} · {d.nome}: {formatCents(d.comissao)} +{" "}
                        {formatCents(d.bonusMeta)} ≠ {formatCents(d.totalGeral)} (diferença de{" "}
                        {formatCents(d.diferenca)})
                      </li>
                    ))}
                    {resultado.divergencias.length > 6 && (
                      <li>e mais {resultado.divergencias.length - 6}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>
              {resultado ? "Fechar" : "Cancelar"}
            </Button>
            <Button onClick={() => void enviar()} disabled={!arquivo || loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {loading ? "Processando..." : "Importar"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
